// Градиент легенды и чипов — СОБИРАЕТСЯ из опор палитры, приехавшей с сервера.
//
// Здесь лежал словарь «имя палитры → CSS-градиент», нарисованный от руки. Он и
// был вторым описанием цвета: у батиметрии тайл красился восемью опорами, а
// легенда рисовала четыре, у течений имя разъезжалось с заливкой. Цвет теперь
// один и живёт в `src/palettes/registry.py`; сюда приезжают числа.
import type { LayerPalette } from '../api'

const NO_PALETTE = 'linear-gradient(to right,rgb(var(--surface-raised)),rgb(var(--text-faint)))'

function rgb(c: number[]): string {
  return `rgb(${c[0]},${c[1]},${c[2]})`
}

/** Доли опор на шкале 0..1 — цвет обязан стоять там, где стоит его значение.
 *
 *  Ровная раскладка соврала бы на любой неравномерной шкале: у хлорофилла опоры
 *  0,1,2,3,5 — последний отрезок вдвое длиннее прочих, и градиент «по пятым»
 *  показывал бы цвет 3 мг/м³ там, где на карте 4.
 */
function offsets(stops: number[]): number[] {
  const lo = stops[0]
  const span = stops[stops.length - 1] - lo
  if (!Number.isFinite(span) || span <= 0) return stops.map((_, i) => i / (stops.length - 1))
  return stops.map(s => (s - lo) / span)
}

/** CSS-градиент палитры слоя. */
export function paletteGradient(palette: LayerPalette | undefined): string {
  if (!palette || palette.colors.length < 2) return NO_PALETTE
  const { colors, stops, discrete } = palette

  if (discrete) {
    // Дискретная шкала (туман: чисто / дымка / туман) — равные полосы с резкой
    // границей. Градиент здесь врал бы: промежуточного состояния нет.
    const width = 100 / colors.length
    const bands = colors.map(
      (c, i) => `${rgb(c)} ${(i * width).toFixed(4)}% ${((i + 1) * width).toFixed(4)}%`,
    )
    return `linear-gradient(to right,${bands.join(',')})`
  }

  const pos = offsets(stops)
  const parts = colors.map((c, i) => `${rgb(c)} ${(pos[i] * 100).toFixed(4)}%`)
  return `linear-gradient(to right,${parts.join(',')})`
}

/** Цвет палитры в доле шкалы 0..1 — линейная интерполяция между опорами.
 *
 *  Тем же приёмом красит тайлы бэкенд (`np.interp` по каналам), поэтому образец
 *  в интерфейсе и пиксель на карте сходятся, а не «выглядят похоже».
 */
export function paletteColorAt(palette: LayerPalette, t: number): string {
  const pos = offsets(palette.stops)
  const x = Math.min(1, Math.max(0, t))
  let i = pos.findIndex((p, k) => k > 0 && p >= x)
  if (i < 1) i = 1
  const span = pos[i] - pos[i - 1] || 1
  const f = (x - pos[i - 1]) / span
  const a = palette.colors[i - 1]
  const b = palette.colors[i]
  return rgb([0, 1, 2].map(k => Math.round(a[k] + f * (b[k] - a[k]))))
}
